import React, { useMemo, useState } from 'react';
import { SwipeableScheduleCard } from './SwipeableScheduleCard';
import { CalendarIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useEvent } from '../contexts/EventContext';
import { useMySchedule } from '../contexts/MyScheduleContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Session, Meeting } from '../types';
interface MyScheduleProps {
  onBack: () => void;
  onNavigateToMeetingDetails?: (meetingId: string) => void;
}
interface ScheduleItem {
  id: string;
  type: 'session' | 'meeting';
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  status?: 'booked' | 'confirmed' | 'pending' | 'declined';
  data: Session | Meeting;
  featureName?: string;
  featureColor?: {
    bg: string;
    text: string;
  };
}
interface HeatMapData {
  day: string;
  dayLabel: string;
  hours: {
    hour: number;
    count: number;
  }[];
}
export function MySchedule({
  onBack,
  onNavigateToMeetingDetails
}: MyScheduleProps) {
  const {
    currentEvent
  } = useEvent();
  const {
    t
  } = useLanguage();
  const {
    bookedSessions,
    meetings,
    removeBookedSession,
    updateMeeting,
    removeMeeting
  } = useMySchedule();
  const [selectedDay, setSelectedDay] = useState('all');
  const [showHeatMap, setShowHeatMap] = useState(true);
  // Feature color mapping
  const getFeatureColor = (featureId: string): {
    bg: string;
    text: string;
  } => {
    const featureColors: Record<string, {
      bg: string;
      text: string;
    }> = {
      // Dubai Active Industry features
      xpro: {
        bg: '#9333ea',
        text: '#ffffff'
      },
      'future-wellness': {
        bg: '#ec4899',
        text: '#ffffff'
      },
      'me-fitness': {
        bg: '#000000',
        text: '#ffffff'
      },
      'mena-roundtable': {
        bg: '#0891b2',
        text: '#ffffff'
      },
      // Dubai Active Show features
      'active-arena': {
        bg: '#17313a',
        text: '#ffffff'
      },
      'main-stage': {
        bg: '#c8e1d9',
        text: '#17313a'
      },
      'yoga-pilates': {
        bg: '#f8c8dc',
        text: '#17313a'
      },
      'dubai-active-talks': {
        bg: '#0f4233',
        text: '#ffffff'
      },
      // Dubai Muscle Show features
      'muscle-classic': {
        bg: '#ef4444',
        text: '#ffffff'
      },
      'muscle-talks': {
        bg: '#dc2626',
        text: '#ffffff'
      },
      fmg: {
        bg: '#f97316',
        text: '#ffffff'
      },
      'team-nogueira': {
        bg: '#ea580c',
        text: '#ffffff'
      },
      'meet-greet': {
        bg: '#fb923c',
        text: '#ffffff'
      }
    };
    return featureColors[featureId] || {
      bg: '#6b7280',
      text: '#ffffff'
    };
  };
  // Get feature name from Agenda features
  const getFeatureName = (featureId: string): string => {
    const featureNames: Record<string, string> = {
      // Dubai Active Industry
      xpro: 'XPRO Summit',
      'future-wellness': 'Future Wellness Summit',
      'me-fitness': 'ME Fitness Business Summit',
      'mena-roundtable': 'MENA Roundtable',
      // Dubai Active Show
      'active-arena': 'Active Arena',
      'main-stage': 'Main Fitness Stage',
      'yoga-pilates': 'Yoga & Pilates Studio',
      'dubai-active-talks': 'Dubai Active Talks',
      // Dubai Muscle Show
      'muscle-classic': 'Dubai Muscle Classic',
      'muscle-talks': 'Dubai Muscle Talks',
      fmg: 'FMG International',
      'team-nogueira': 'Team Nogueira Future Champions',
      'meet-greet': 'Athlete Meet & Greet'
    };
    return featureNames[featureId] || 'Session';
  };
  // Add day filter options
  const days = [{
    id: 'all',
    label: t('agenda.allDays'),
    date: ''
  }, {
    id: 'day1',
    label: `${t('agenda.day')} 1`,
    date: '24 Oct'
  }, {
    id: 'day2',
    label: `${t('agenda.day')} 2`,
    date: '25 Oct'
  }, {
    id: 'day3',
    label: `${t('agenda.day')} 3`,
    date: '26 Oct'
  }];
  // Helper to get day from date
  const getSessionDay = (dateStr: string): string => {
    // Handle both "October 24, 2025" and "24 Oct" formats
    if (dateStr.includes('October 24') || dateStr.includes('24 Oct')) return 'day1';
    if (dateStr.includes('October 25') || dateStr.includes('25 Oct')) return 'day2';
    if (dateStr.includes('October 26') || dateStr.includes('26 Oct')) return 'day3';
    return 'day1';
  };
  // Combine sessions and meetings into unified schedule
  const scheduleItems = useMemo(() => {
    const items: ScheduleItem[] = [];
    bookedSessions.forEach(session => {
      items.push({
        id: session.id,
        type: 'session',
        title: session.title,
        date: session.date,
        startTime: session.startTime,
        endTime: session.endTime,
        location: session.location,
        status: 'booked',
        data: session,
        featureName: getFeatureName(session.feature),
        featureColor: getFeatureColor(session.feature)
      });
    });
    meetings.forEach(meeting => {
      items.push({
        id: meeting.id,
        type: 'meeting',
        title: `Meeting with ${meeting.attendeeName}`,
        date: meeting.date,
        startTime: meeting.startTime,
        endTime: meeting.endTime,
        location: meeting.location,
        status: meeting.status,
        data: meeting
      });
    });
    const filtered = selectedDay === 'all' ? items : items.filter(item => getSessionDay(item.date) === selectedDay);
    return filtered.sort((a, b) => {
      const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
      if (dateCompare !== 0) return dateCompare;
      return a.startTime.localeCompare(b.startTime);
    });
  }, [bookedSessions, meetings, selectedDay]);
  // Calculate heat map data - filtered by selected day
  const heatMapData = useMemo(() => {
    const dayMap: Record<string, HeatMapData> = {
      day1: {
        day: 'day1',
        dayLabel: '24 Oct',
        hours: []
      },
      day2: {
        day: 'day2',
        dayLabel: '25 Oct',
        hours: []
      },
      day3: {
        day: 'day3',
        dayLabel: '26 Oct',
        hours: []
      }
    };
    // Initialize hours 9am-6pm for each day
    Object.keys(dayMap).forEach(day => {
      for (let hour = 9; hour <= 18; hour++) {
        dayMap[day].hours.push({
          hour,
          count: 0
        });
      }
    });
    // Create all items array (both sessions and meetings)
    const allItems: ScheduleItem[] = [];
    bookedSessions.forEach(session => {
      allItems.push({
        id: session.id,
        type: 'session',
        title: session.title,
        date: session.date,
        startTime: session.startTime,
        endTime: session.endTime,
        location: session.location,
        status: 'booked',
        data: session,
        featureName: getFeatureName(session.feature),
        featureColor: getFeatureColor(session.feature)
      });
    });
    meetings.forEach(meeting => {
      allItems.push({
        id: meeting.id,
        type: 'meeting',
        title: `Meeting with ${meeting.attendeeName}`,
        date: meeting.date,
        startTime: meeting.startTime,
        endTime: meeting.endTime,
        location: meeting.location,
        status: meeting.status,
        data: meeting
      });
    });
    // Count activities per hour
    allItems.forEach(item => {
      const day = getSessionDay(item.date);
      // Parse start and end times (format: "HH:MM")
      const [startHourStr, startMinStr] = item.startTime.split(':');
      const [endHourStr, endMinStr] = item.endTime.split(':');
      const startHour = parseInt(startHourStr);
      const endHour = parseInt(endHourStr);
      const endMin = parseInt(endMinStr);
      if (dayMap[day]) {
        // Count for each hour the activity spans
        for (let hour = startHour; hour <= endHour; hour++) {
          // Don't count the end hour if the activity ends at exactly that hour (e.g., 14:00)
          if (hour === endHour && endMin === 0) {
            continue;
          }
          const hourData = dayMap[day].hours.find(h => h.hour === hour);
          if (hourData && hour >= 9 && hour <= 18) {
            hourData.count++;
          }
        }
      }
    });
    // Filter by selected day
    if (selectedDay === 'all') {
      return Object.values(dayMap);
    } else {
      return [dayMap[selectedDay]];
    }
  }, [bookedSessions, meetings, selectedDay]);
  // Get max count for scaling
  const maxCount = useMemo(() => {
    return Math.max(...heatMapData.flatMap(day => day.hours.map(h => h.count)), 1);
  }, [heatMapData]);
  // Get heat map color intensity based on event theme - BACK TO ORIGINAL COLORS
  const getHeatColor = (count: number) => {
    if (count === 0) return 'bg-gray-100';
    const intensity = count / maxCount;
    // Use event-specific colors
    if (currentEvent.id === 'active') {
      if (intensity <= 0.25) return 'bg-[#c8e1d9]';
      if (intensity <= 0.5) return 'bg-[#9bc4b8]';
      if (intensity <= 0.75) return 'bg-[#4a8270]';
      return 'bg-[#17313a]';
    } else if (currentEvent.id === 'muscle') {
      if (intensity <= 0.25) return 'bg-red-200';
      if (intensity <= 0.5) return 'bg-red-400';
      if (intensity <= 0.75) return 'bg-red-600';
      return 'bg-red-800';
    } else {
      // Industry - use blue tones
      if (intensity <= 0.25) return 'bg-blue-200';
      if (intensity <= 0.5) return 'bg-blue-400';
      if (intensity <= 0.75) return 'bg-blue-600';
      return 'bg-blue-800';
    }
  };
  // Group items by date
  const itemsByDate = useMemo(() => {
    return scheduleItems.reduce((acc, item) => {
      if (!acc[item.date]) {
        acc[item.date] = [];
      }
      acc[item.date].push(item);
      return acc;
    }, {} as Record<string, ScheduleItem[]>);
  }, [scheduleItems]);
  // Check for schedule conflicts and identify conflicting items
  const conflictingItemIds = useMemo(() => {
    const conflicts = new Set<string>();
    Object.values(itemsByDate).forEach(dateItems => {
      for (let i = 0; i < dateItems.length; i++) {
        for (let j = i + 1; j < dateItems.length; j++) {
          const item1 = dateItems[i];
          const item2 = dateItems[j];
          const start1 = parseInt(item1.startTime.replace(':', ''));
          const end1 = parseInt(item1.endTime.replace(':', ''));
          const start2 = parseInt(item2.startTime.replace(':', ''));
          const end2 = parseInt(item2.endTime.replace(':', ''));
          if (start1 < end2 && end1 > start2) {
            conflicts.add(item1.id);
            conflicts.add(item2.id);
          }
        }
      }
    });
    return conflicts;
  }, [itemsByDate]);
  const hasConflicts = conflictingItemIds.size > 0;
  const getTextColor = () => {
    if (currentEvent.id === 'active') {
      return '#17313a';
    }
    return 'white';
  };
  const handleRemoveSession = (sessionId: string) => {
    removeBookedSession(sessionId);
  };
  const handleManageMeeting = (meetingId: string) => {
    if (onNavigateToMeetingDetails) {
      onNavigateToMeetingDetails(meetingId);
    }
  };
  const handleAcceptMeeting = (meetingId: string) => {
    updateMeeting(meetingId, {
      status: 'confirmed'
    });
  };
  const handleDeclineMeeting = (meetingId: string) => {
    removeMeeting(meetingId);
  };
  // Get meeting status label (matching Networking component)
  const getMeetingStatusLabel = (meeting: Meeting) => {
    if (meeting.status === 'confirmed') return 'Confirmed';
    if (meeting.requestedBy === 'me') return 'Awaiting Response';
    return 'Respond to Invite';
  };
  // Get meeting status badge variant
  const getMeetingStatusVariant = (meeting: Meeting): 'success' | 'warning' | 'default' => {
    if (meeting.status === 'confirmed') return 'success';
    if (meeting.requestedBy === 'me') return 'warning';
    return 'default';
  };
  // Calculate total items for display
  const totalItems = bookedSessions.length + meetings.length;
  return <div className="pb-24" data-id="element-3682">
      {/* Day Filter - adjusted sticky position */}
      <div className="sticky top-[88px] z-30 bg-white border-b border-gray-200 px-4 pt-4 pb-3" data-id="element-3683">
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4" data-id="element-3684">
          {days.map(day => <button key={day.id} onClick={() => setSelectedDay(day.id)} className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium font-gotham transition-all ${selectedDay === day.id ? 'text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} style={selectedDay === day.id ? {
          backgroundColor: currentEvent.colors.primary,
          color: getTextColor()
        } : {}} data-id="element-3685">
              <div className="text-sm" data-id="element-3686">{day.label}</div>
              {day.date && <div className="text-xs opacity-90" data-id="element-3687">{day.date}</div>}
            </button>)}
        </div>
      </div>

      {/* Heat Map - Only show if there are items */}
      {totalItems > 0 && <div className="mx-4 mt-4 bg-white border border-gray-200 rounded-lg p-4" data-id="element-3688">
          <div className="flex items-center justify-between" data-id="element-3689">
            <h3 className="text-sm font-bold text-gray-900 font-gotham" data-id="element-3690">
              Activity Heat Map
            </h3>
            <button onClick={() => setShowHeatMap(!showHeatMap)} className="p-1 hover:bg-gray-100 rounded transition-colors" aria-label={showHeatMap ? 'Hide heat map' : 'Show heat map'} data-id="element-3691">
              {showHeatMap ? <ChevronUpIcon className="w-5 h-5 text-gray-600" data-id="element-3692" /> : <ChevronDownIcon className="w-5 h-5 text-gray-600" data-id="element-3693" />}
            </button>
          </div>

          {showHeatMap && <>
              <div className="space-y-2 mt-3" data-id="element-3694">
                {heatMapData.map(dayData => <div key={dayData.day} data-id="element-3695">
                    <div className="text-xs font-semibold text-gray-600 mb-1 font-gotham" data-id="element-3696">
                      {dayData.dayLabel}
                    </div>
                    <div className="flex gap-1" data-id="element-3697">
                      {dayData.hours.map(hourData => <div key={hourData.hour} className="relative group flex-1" style={{
                minWidth: '8px'
              }} data-id="element-3698">
                          <div className={`h-8 rounded transition-all cursor-pointer hover:opacity-80 ${getHeatColor(hourData.count)}`} title={`${hourData.hour}:00 - ${hourData.count} ${hourData.count === 1 ? 'activity' : 'activities'}`} data-id="element-3699" />
                          {/* Tooltip on hover */}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10" data-id="element-3700">
                            <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap font-gotham" data-id="element-3701">
                              {hourData.hour}:00
                              <br data-id="element-3702" />
                              {hourData.count}{' '}
                              {hourData.count === 1 ? 'item' : 'items'}
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>)}
              </div>
              <div className="flex items-center justify-between mt-3 text-xs text-gray-500 font-gotham" data-id="element-3703">
                <span data-id="element-3704">9am</span>
                <span data-id="element-3705">12pm</span>
                <span data-id="element-3706">3pm</span>
                <span data-id="element-3707">6pm</span>
              </div>
              <div className="flex items-center gap-2 mt-3 text-xs text-gray-600 font-gotham" data-id="element-3708">
                <span data-id="element-3709">Less busy</span>
                <div className="flex gap-1" data-id="element-3710">
                  <div className="w-4 h-4 rounded bg-gray-100" data-id="element-3711"></div>
                  <div className={`w-4 h-4 rounded ${currentEvent.id === 'active' ? 'bg-[#c8e1d9]' : currentEvent.id === 'muscle' ? 'bg-red-200' : 'bg-blue-200'}`} data-id="element-3712"></div>
                  <div className={`w-4 h-4 rounded ${currentEvent.id === 'active' ? 'bg-[#9bc4b8]' : currentEvent.id === 'muscle' ? 'bg-red-400' : 'bg-blue-400'}`} data-id="element-3713"></div>
                  <div className={`w-4 h-4 rounded ${currentEvent.id === 'active' ? 'bg-[#4a8270]' : currentEvent.id === 'muscle' ? 'bg-red-600' : 'bg-blue-600'}`} data-id="element-3714"></div>
                  <div className={`w-4 h-4 rounded ${currentEvent.id === 'active' ? 'bg-[#17313a]' : currentEvent.id === 'muscle' ? 'bg-red-800' : 'bg-blue-800'}`} data-id="element-3715"></div>
                </div>
                <span data-id="element-3716">More busy</span>
              </div>
            </>}
        </div>}

      {/* Conflict Warning */}
      {hasConflicts && <div className="mx-4 mt-4 bg-red-50 border-2 border-red-500 rounded-lg p-3" data-id="element-3717">
          <p className="text-sm font-bold text-red-900 font-gotham" data-id="element-3718">
            ⚠️ {t('schedule.conflictDetected')}
          </p>
          <p className="text-xs text-red-800 font-gotham mt-1" data-id="element-3719">
            {t('schedule.overlappingItems')}
          </p>
        </div>}

      {scheduleItems.length === 0 ? <div className="p-8 text-center" data-id="element-3720">
          <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" data-id="element-3721" />
          <h3 className="text-lg font-bold text-gray-900 font-gotham mb-2" data-id="element-3722">
            {t('schedule.noItems')}
          </h3>
          <p className="text-gray-600 font-gotham" data-id="element-3723">
            {t('schedule.bookSessions')}
          </p>
        </div> : <div className="px-4 py-4 space-y-6" data-id="element-3724">
          {Object.entries(itemsByDate).map(([date, dateItems]) => <div key={date} data-id="element-3725">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3 font-gotham" data-id="element-3726">
                {new Date(date).toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
          })}
              </h3>

              <div className="space-y-3" data-id="element-3727">
                {dateItems.map(item => <SwipeableScheduleCard key={item.id} item={item} primaryColor={currentEvent.colors.primary} onRemoveSession={handleRemoveSession} onManageMeeting={handleManageMeeting} onAcceptMeeting={handleAcceptMeeting} onDeclineMeeting={handleDeclineMeeting} hasConflict={conflictingItemIds.has(item.id)} data-id="element-3728" />)}
              </div>
            </div>)}
        </div>}
    </div>;
}